import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiResponse,
  EmployerProfile,
  ExportDataStatus,
  TouStatusData,
  UserDataTableRow,
  UserProfile,
} from '../../common/types';
import useStore from '../../stores';
import axiosInstance from '../../utils/axiosInstance';

// Query Keys
export const userKeys = {
  all: ['users'] as const,
  profile: () => [...userKeys.all, 'profile'] as const,
  employer: () => [...userKeys.all, 'employer'] as const,
  tou: () => [...userKeys.all, 'tou'] as const,
  list: () => [...userKeys.all, 'list'] as const,
  employees: (funderId: string) =>
    [...userKeys.all, 'employees', funderId] as const,
  exportStatus: () => [...userKeys.all, 'exportStatus'] as const,
};

// User Profile Hooks
export const useUserProfile = () =>
  useQuery({
    queryKey: userKeys.profile(),
    queryFn: async (): Promise<UserProfile> => {
      const response = await axiosInstance.get<ApiResponse<UserProfile>>(
        '/api/employee/detail/',
      );
      if (!response.data.success) {
        throw new Error(response.data.error.message);
      }
      return response.data.data;
    },
    retry: 1,
  });

export const useEmployerProfile = (funderId: string | undefined) =>
  useQuery({
    queryKey: userKeys.employer(),
    queryFn: async (): Promise<EmployerProfile> => {
      if (!funderId) {
        throw new Error('Funder ID is required');
      }
      const response = await axiosInstance.get<ApiResponse<EmployerProfile>>(
        '/api/funder/detail/',
        {
          params: {
            funder_id: funderId,
          },
        },
      );
      if (!response.data.success) {
        throw new Error(response.data.error.message);
      }
      return response.data.data;
    },
    retry: 1,
    enabled: !!funderId, // Only run query when funderId is available
  });

export const useTouStatus = () =>
  useQuery({
    queryKey: userKeys.tou(),
    queryFn: async (): Promise<TouStatusData> => {
      const response = await axiosInstance.get<ApiResponse<TouStatusData>>(
        '/api/get-tou-acceptance/',
      );
      if (!response.data.success) {
        throw new Error(response.data.error.message);
      }
      return response.data.data;
    },
  });

export const useUserList = () =>
  useQuery({
    queryKey: userKeys.list(),
    queryFn: async (): Promise<UserDataTableRow[]> => {
      const response = await axiosInstance.get<ApiResponse<UserDataTableRow[]>>(
        '/api/get-all-users/',
      );
      if (!response.data.success) {
        throw new Error(response.data.error.message);
      }
      return response.data.data;
    },
  });

export const useEmployeesByFunder = (funderId: string | undefined) =>
  useQuery({
    queryKey: userKeys.employees(funderId || ''),
    queryFn: async (): Promise<UserDataTableRow[]> => {
      if (!funderId) {
        throw new Error('Funder ID is required');
      }
      const response = await axiosInstance.get<ApiResponse<UserDataTableRow[]>>(
        `/api/employees/?funder_id=${funderId}`,
      );
      if (!response.data.success) {
        throw new Error(response.data.error.message);
      }
      return response.data.data;
    },
    enabled: !!funderId, // Only run query when funderId is available
  });

export const useExportStatus = () =>
  useQuery({
    queryKey: userKeys.exportStatus(),
    queryFn: async (): Promise<ExportDataStatus[]> => {
      const response = await axiosInstance.get<ApiResponse<ExportDataStatus[]>>(
        '/api/export-status/',
      );
      if (!response.data.success) {
        throw new Error(response.data.error.message);
      }
      return response.data.data;
    },
  });

// Mutation Hooks
export const useUpdateUserProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (profileData: Partial<UserProfile>) => {
      const response = await axiosInstance.post<ApiResponse<UserProfile>>(
        '/api/employee/update/',
        profileData,
      );
      if (!response.data.success) {
        throw new Error(response.data.error.message);
      }
      return response.data.data;
    },
    onSuccess: (updatedProfile) => {
      // Update the cached profile
      queryClient.setQueryData(userKeys.profile(), updatedProfile);
      // Invalidate user list to update any changes there too
      queryClient.invalidateQueries({ queryKey: userKeys.list() });
    },
  });
};

export const useUpdateEmployerProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (profileData: Partial<EmployerProfile>) => {
      const response = await axiosInstance.post<ApiResponse<EmployerProfile>>(
        '/api/funder/update/',
        profileData,
      );
      if (!response.data.success) {
        throw new Error(response.data.error.message);
      }
      return response.data.data;
    },
    onSuccess: (updatedProfile) => {
      // Update the cached employer profile
      queryClient.setQueryData(userKeys.employer(), updatedProfile);
    },
  });
};

export const useAcceptTou = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response =
        await axiosInstance.post<ApiResponse<null>>('/api/accept-tou/');
      if (!response.data.success) {
        throw new Error(response.data.error.message);
      }
      return response.data.data;
    },
    onSuccess: () => {
      // Update the store here (not in a mutate()-level callback): hook-level
      // onSuccess still runs if the Tou page unmounts while the POST is in
      // flight, which App.tsx's loading gate can do at any time. A dropped
      // callback left touAccepted=false and looped the user back to /tou.
      useStore.getState().setTouAccepted(true);
      // Invalidate TOU status
      queryClient.invalidateQueries({ queryKey: userKeys.tou() });
    },
  });
};

// Setup mutations
export const useSetupUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (setupData: Partial<UserProfile>) => {
      const response = await axiosInstance.post<ApiResponse<UserProfile>>(
        '/api/employee/setup/',
        setupData,
      );
      if (!response.data.success) {
        throw new Error(response.data.error.message);
      }
      return response.data.data;
    },
    onSuccess: () => {
      // Invalidate profile queries to refetch updated data
      queryClient.invalidateQueries({ queryKey: userKeys.profile() });
      queryClient.invalidateQueries({ queryKey: userKeys.employer() });
    },
  });
};

// User management mutations (admin functions)
export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userData: {
      employee_email?: string;
      first_name?: string;
      last_name?: string;
      role?: string;
      read_only?: boolean;
      is_admin?: boolean;
    }) => {
      const response = await axiosInstance.post<ApiResponse<UserProfile>>(
        '/api/user/update/',
        userData,
      );
      if (!response.data.success) {
        throw new Error(response.data.error.message);
      }
      return response.data.data;
    },
    onSuccess: () => {
      // Refresh user list and profile
      queryClient.invalidateQueries({ queryKey: userKeys.list() });
      queryClient.invalidateQueries({ queryKey: userKeys.profile() });
    },
  });
};

export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userData: {
      employee_id?: string;
      funder_id?: string;
      user_email?: string;
      first_name: string;
      last_name: string;
      read_only: boolean;
      is_admin: boolean;
    }) => {
      const response = await axiosInstance.post<ApiResponse<UserProfile>>(
        '/api/employee/create/',
        userData,
      );
      if (!response.data.success) {
        throw new Error(response.data.error.message);
      }
      return response.data.data;
    },
    onSuccess: () => {
      // Refresh user list
      queryClient.invalidateQueries({ queryKey: userKeys.list() });
    },
  });
};

export const useDeleteUserByEmail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (employeeEmail: string) => {
      const response = await axiosInstance.post<
        ApiResponse<{ success: boolean }>
      >('/api/user/delete/', { employee_email: employeeEmail });
      if (!response.data.success) {
        throw new Error(response.data.error.message);
      }
      return response.data.data;
    },
    onSuccess: () => {
      // Refresh user list
      queryClient.invalidateQueries({ queryKey: userKeys.list() });
    },
  });
};

export const useReactivateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userData: {
      user_email: string;
      first_name?: string;
      last_name?: string;
      is_admin?: boolean;
      read_only?: boolean;
    }) => {
      const response = await axiosInstance.post<ApiResponse<UserProfile>>(
        '/api/user/reactivate/',
        userData,
      );
      if (!response.data.success) throw new Error(response.data.error.message);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.list() });
    },
  });
};

export const useInviteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (inviteData: {
      email: string;
      role: string;
      [key: string]: string;
    }) => {
      const response = await axiosInstance.post<ApiResponse<null>>(
        '/api/invite-user/',
        inviteData,
      );
      if (!response.data.success) {
        throw new Error(response.data.error.message);
      }
      return response.data.data;
    },
    onSuccess: () => {
      // Refresh user list
      queryClient.invalidateQueries({ queryKey: userKeys.list() });
    },
  });
};

export const useUpdateUserRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }) => {
      const response = await axiosInstance.post<ApiResponse<null>>(
        '/api/update-user-role/',
        { user_id: userId, role },
      );
      if (!response.data.success) {
        throw new Error(response.data.error.message);
      }
      return response.data.data;
    },
    onSuccess: () => {
      // Refresh user list
      queryClient.invalidateQueries({ queryKey: userKeys.list() });
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const response = await axiosInstance.delete<ApiResponse<null>>(
        `/api/delete-user/${userId}/`,
      );
      if (!response.data.success) {
        throw new Error(response.data.error.message);
      }
      return response.data.data;
    },
    onSuccess: () => {
      // Refresh user list
      queryClient.invalidateQueries({ queryKey: userKeys.list() });
    },
  });
};

export const useExportAll = () =>
  useMutation({
    mutationFn: async (exportType: string = 'funder'): Promise<void> => {
      const response = await axiosInstance.get<ApiResponse<void>>(
        '/api/get-document/export/',
        {
          params: {
            export_type: exportType,
          },
        },
      );
      if (!response.data.success) {
        throw new Error(response.data.error.message);
      }
      return response.data.data;
    },
  });
